/** Composer chrome: send hint + the reattach send block. Enter sends and Shift+Enter inserts a newline — both are ChatBox's native behaviour. */

import { createContext } from "react"
import type { KeyboardEvent, MutableRefObject, ReactNode } from "react"
import { Box, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import type { SystemStyleObject } from "@mui/system"
import { chatComposerClasses, chatConversationClasses } from "@mui/x-chat"

export const COMPOSER_HINT_ID = "kelly-composer-send-hint"

export const COMPOSER_PLACEHOLDER = "Ask about your data (e.g. 'What are my position sizes?')"

/** Composer bottom inset and opaque footer-band height, so the band spans card-bottom→page-bottom, never above the card. */
export const DISCLAIMER_STRIP = "2.5rem"
/** Must exceed the composer card + DISCLAIMER_STRIP so the last turn clears the overlay. */
export const CONVERSATION_BOTTOM_PADDING = "13rem"

/** Shared centred-column measure for the whole conversation area (message list, composer card, disclaimer band). */
export const CONVERSATION_COLUMN_MAX_WIDTH = "57.5rem"

/**
 * Inline inset for everything floating over the transcript, so the message list's scroll bar stays visible and
 * grabbable. Deliberately a fixed length, not a container-relative one: the app's root font-size is 12px, so
 * this is already 12px, and `@mui/x-chat`'s ChatBox root is itself a size container — a `cqi` gutter would
 * resolve against the library's box inside ChatBox and against ours outside it.
 */
export const SCROLLBAR_GUTTER = "1rem"

/** Written by useTranscriptScroll: measured distance from the conversation's bottom edge to the composer card's top. */
export const COMPOSER_CLEARANCE_VAR = "--kelly-composer-clearance"
/** Live composer clearance, falling back to the static lower bound for the frame before the first measurement. */
export const COMPOSER_CLEARANCE = `var(${COMPOSER_CLEARANCE_VAR}, ${CONVERSATION_BOTTOM_PADDING})`
/** Band above the composer reserved for floating status chrome (working cue, jump-to-latest). */
export const TRANSCRIPT_CHROME_BAND = "3.5rem"
/** Bottom offset every floating piece of chrome sits at, so the pills and the jump control cannot drift apart. */
export const TRANSCRIPT_CHROME_BOTTOM = `calc(${COMPOSER_CLEARANCE} + 0.5rem)`

export const COMPOSER_CHROME_SX: SystemStyleObject<Theme> = {
  // Positioning context for the composer overlay.
  [`& .${chatConversationClasses.root}`]: { position: "relative" },
  // Floating opaque card; marginBottom:0 drops the library's residual gap. Border+shadow read it against the same-paper band.
  [`& .${chatComposerClasses.root}`]: {
    position: "absolute",
    insetInline: SCROLLBAR_GUTTER,
    bottom: DISCLAIMER_STRIP,
    marginBottom: 0,
    width: "auto",
    maxWidth: CONVERSATION_COLUMN_MAX_WIDTH,
    marginInline: "auto",
    borderRadius: "0.875rem",
    backgroundColor: "var(--app-bg-paper)",
    border: "1px solid var(--opcolor-grey-lighter)",
    boxShadow: "var(--mui-shadows-3)",
    "&:focus-within": { borderColor: "var(--app-primary-filled)" },
    zIndex: 2,
  },
  // Match the app body size (x-chat resets to 12px).
  [`& .${chatComposerClasses.textArea}`]: { fontSize: "inherit", flex: "0 0 auto", maxHeight: "calc(8 * 1.43em + 8px)" },
  [`& .${chatComposerClasses.sendButton}:not(:disabled)`]: {
    backgroundColor: "var(--app-primary-filled)",
    color: "var(--mui-palette-primary-contrastText)",
    "&:hover": { backgroundColor: "var(--mui-palette-primary-dark)" },
  },
  // Pin full opacity: the library's disabledOpacity compounds until the glyph vanishes into the circle.
  [`& .${chatComposerClasses.sendButton}:disabled`]: {
    opacity: 1,
    backgroundColor: "var(--mui-palette-action-disabledBackground)",
    color: "var(--mui-palette-action-disabled)",
  },
}

export interface ComposerControlsValue {
  isReattaching: boolean
  /** Server-side stream cancel (APP-3394); no-op until #4141. */
  stopServerStream: () => void
  // The gate lives below ChatBox but the submit handlers live above it, so the two meet on a ref.
  attachmentBlockedRef: MutableRefObject<boolean>
}
const DEFAULT_CONTROLS: ComposerControlsValue = {
  isReattaching: false,
  stopServerStream: () => undefined,
  attachmentBlockedRef: { current: false },
}
export const ComposerControlsContext = createContext<ComposerControlsValue>(DEFAULT_CONTROLS)

const SEND_HINT = "Shift + Enter for a new line"

const TOOLBAR_SX = { position: "relative", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, px: 0.5, pb: 0.5 } satisfies SxProps<Theme>
const HINT_SX = {
  position: "absolute",
  insetInline: "3rem",
  bottom: 0,
  textAlign: "center",
  color: "text.secondary",
  userSelect: "none",
  pointerEvents: "none",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} satisfies SxProps<Theme>

export function ComposerToolbarWithHint({ children }: { children?: ReactNode }) {
  return <Box sx={TOOLBAR_SX}>
    {children}
    <Typography id={COMPOSER_HINT_ID} component="span" variant="caption" sx={HINT_SX}>{SEND_HINT}</Typography>
  </Box>
}

/** Sending is ChatBox's own; this only suppresses it while a reattach is in flight. */
export function handleComposerKeyDown(event: KeyboardEvent<HTMLTextAreaElement>, { blockSubmit = false }: { blockSubmit?: boolean } = {}) {
  if (!blockSubmit || event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return
  event.preventDefault()
}
